import React from "react";
import * as Popover from "@radix-ui/react-popover";
import styled from "styled-components";

// Right-click action menu used by the drawing surface and gallery cards.
// Anchored via Radix's virtualRef API: Floating UI reads
// getBoundingClientRect() on every position update, so the anchor lives
// purely as a measurable — no DOM element to be hijacked by a
// transform/filter/backdrop-filter ancestor's containing block.
// (GalleryPanel uses backdrop-filter, which would warp position:fixed
// descendants — this avoids that trap entirely.)
//
// Caller owns open state + position. We render Radix's Portal so the
// menu can escape any clipping ancestors (gallery card overflow,
// drawing-area FitContainer, etc.).
function CompositeActionMenu({
  open,
  onOpenChange,
  anchorPos,
  onDownload,
  onCopy,
  canCopy,
}) {
  // Stable virtualRef object; .current.getBoundingClientRect closes over
  // the latest anchorPos via the ref written below, so Floating UI sees
  // up-to-date coords on every open without us re-creating the ref.
  const anchorPosRef = React.useRef(anchorPos);
  anchorPosRef.current = anchorPos;
  const virtualRef = React.useRef({
    getBoundingClientRect() {
      const p = anchorPosRef.current;
      const x = p?.x ?? 0;
      const y = p?.y ?? 0;
      return {
        x,
        y,
        left: x,
        top: y,
        right: x,
        bottom: y,
        width: 0,
        height: 0,
        toJSON() {
          return this;
        },
      };
    },
  });

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor virtualRef={virtualRef} />
      <Popover.Portal>
        <Content
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Item
            type="button"
            onClick={() => {
              onOpenChange(false);
              onDownload();
            }}
          >
            Download
          </Item>
          {canCopy && (
            <Item
              type="button"
              onClick={() => {
                onOpenChange(false);
                onCopy();
              }}
            >
              Copy to clipboard
            </Item>
          )}
        </Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const Content = styled(Popover.Content)`
  min-width: 180px;
  padding: 4px;
  background: var(--color-surface);
  border: 1px solid var(--color-orange-300);
  border-radius: 6px;
  box-shadow: var(--surface-box-shadow-strong);
  display: flex;
  flex-direction: column;
  z-index: 30;
`;

const Item = styled.button`
  appearance: none;
  background: none;
  border: none;
  text-align: left;
  padding: 8px 12px;
  font: inherit;
  font-size: 0.95rem;
  color: var(--color-text);
  border-radius: 4px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--color-orange-100);
    outline: none;
  }
`;

export default CompositeActionMenu;
